import type { FormEvent } from 'react'

import { useState } from 'react'
import { ArrowRight, Check, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ActionButton } from '@/components/generals/button'

type Status = 'idle' | 'loading' | 'success' | 'error'

const budgets = [
    'Under $5,000 / month',
    '$5,000 – $15,000 / month',
    '$15,000 – $50,000 / month',
    '$50,000+ / month'
]

const services = ['Meta Ads', 'Google Ads', 'TikTok Ads', 'Content creation']

const FIELD = 'w-full rounded-xl bg-surface-3 px-4 py-3 text-[15px] tracking-[-0.01em] text-white ring-1 ring-white/10 transition-shadow duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] outline-none placeholder:text-white/35 focus:ring-white/30'
const LABEL = 'text-sm leading-none font-medium tracking-[-0.01em] text-white'
const ERROR = 'text-[13px] tracking-[-0.01em] text-[#ff6a5c]'

const SELECT_CHEVRON = `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2" stroke-linecap="round"><path d="m6 9 6 6 6-6"/></svg>')}")`

export const ContactForm = () => {
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [selected, setSelected] = useState<string[]>([])

    const toggleService = (service: string): void => {
        setSelected((current) => (
            current.includes(service)
                ? current.filter((item) => item !== service)
                : [...current, service]
        ))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'loading') {
            return
        }

        const form = event.currentTarget
        const data = new FormData(form)

        setStatus('loading')
        setErrors({})
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: data.get('name'),
                    email: data.get('email'),
                    company: data.get('company'),
                    monthlyBudget: data.get('monthlyBudget'),
                    services: selected.join(', '),
                    message: data.get('message')
                })
            })

            const body = await response.json()

            if (!response.ok || !body.ok) {
                setStatus('error')
                setErrors(body.errors ?? {})
                setMessage(body.message ?? 'Something went wrong. Please try again.')

                return
            }

            setStatus('success')
            setMessage(body.message)
            setSelected([])
            form.reset()
        } catch {
            setStatus('error')
            setMessage('Something went wrong. Please try again.')
        }
    }

    if (status === 'success') {
        return (
            <div className='flex min-h-[520px] flex-col items-center justify-center gap-5 rounded-2xl bg-surface-1 p-10 text-center ring-1 ring-white/8'>
                <span className='flex size-14 items-center justify-center rounded-full bg-[#8b5cf6] text-white shadow-[0_0_40px_-8px_rgba(139,92,246,0.9)]'>
                    <Check className='size-6' />
                </span>

                <h3 className='text-2xl leading-tight font-medium tracking-[-0.03em] text-white'>
                    Message received
                </h3>

                <p className='max-w-[38ch] text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56'>
                    {message}
                </p>

                <ActionButton
                    variant='secondary'
                    size='small'
                    onClick={() => {
                        setStatus('idle')
                        setMessage('')
                    }}
                >
                    Send another message
                </ActionButton>
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className='flex flex-col gap-5 rounded-2xl bg-surface-1 p-6 ring-1 ring-white/8 sm:p-8'
        >
            <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                    <label
                        htmlFor='name'
                        className={LABEL}
                    >
                        Your name
                    </label>

                    <input
                        id='name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        required
                        placeholder='Jane Mercer'
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                        className={cn(FIELD, errors.name && 'ring-[#ff6a5c]/60')}
                    />

                    {errors.name ? (
                        <p
                            id='name-error'
                            className={ERROR}
                        >
                            {errors.name}
                        </p>
                    ) : null}
                </div>

                <div className='flex flex-col gap-2'>
                    <label
                        htmlFor='email'
                        className={LABEL}
                    >
                        Email address
                    </label>

                    <input
                        id='email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        required
                        placeholder='jane@brand.com'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        className={cn(FIELD, errors.email && 'ring-[#ff6a5c]/60')}
                    />

                    {errors.email ? (
                        <p
                            id='email-error'
                            className={ERROR}
                        >
                            {errors.email}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                    <label
                        htmlFor='company'
                        className={LABEL}
                    >
                        Company

                        <span className='ml-1 font-normal text-white/40'>
                            (optional)
                        </span>
                    </label>

                    <input
                        id='company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        placeholder='Nostalgie'
                        className={FIELD}
                    />
                </div>

                <div className='flex flex-col gap-2'>
                    <label
                        htmlFor='monthlyBudget'
                        className={LABEL}
                    >
                        Monthly ad spend
                    </label>

                    <select
                        id='monthlyBudget'
                        name='monthlyBudget'
                        defaultValue=''
                        className={cn(FIELD, 'appearance-none pr-10')}
                        style={{
                            backgroundImage: SELECT_CHEVRON,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 1rem center',
                            backgroundSize: '16px'
                        }}
                    >
                        <option value=''>
                            Select a range
                        </option>

                        {budgets.map((budget) => (
                            <option
                                key={budget}
                                value={budget}
                            >
                                {budget}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <fieldset className='flex flex-col gap-3'>
                <legend className={cn(LABEL, 'mb-1')}>
                    What are you interested in?
                </legend>

                <div className='flex flex-wrap gap-2'>
                    {services.map((service) => {
                        const active = selected.includes(service)

                        return (
                            <button
                                key={service}
                                type='button'
                                aria-pressed={active}
                                onClick={() => toggleService(service)}
                                className={cn(
                                    'cursor-pointer rounded-xl px-3.5 py-2.5 text-sm leading-none font-medium tracking-[-0.01em] ring-1 transition-all duration-300 ease-[cubic-bezier(0.44,0,0.56,1)]',
                                    active
                                        ? 'bg-[#8b5cf6] text-white ring-[#8b5cf6]'
                                        : 'bg-surface-3 text-white/56 ring-white/10 hover:text-white hover:ring-white/25'
                                )}
                            >
                                {service}
                            </button>
                        )
                    })}
                </div>
            </fieldset>

            <div className='flex flex-col gap-2'>
                <label
                    htmlFor='message'
                    className={LABEL}
                >
                    What are you trying to achieve?
                </label>

                <textarea
                    id='message'
                    name='message'
                    rows={5}
                    required
                    placeholder='Tell us about your brand, your current results and where you want to get to.'
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                    className={cn(FIELD, 'resize-y', errors.message && 'ring-[#ff6a5c]/60')}
                />

                {errors.message ? (
                    <p
                        id='message-error'
                        className={ERROR}
                    >
                        {errors.message}
                    </p>
                ) : null}
            </div>

            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <ActionButton
                    type='submit'
                    disabled={status === 'loading'}
                    className='w-full sm:w-auto'
                >
                    {status === 'loading' ? (
                        <Loader2
                            aria-hidden='true'
                            className='size-4 animate-spin'
                        />
                    ) : null}

                    {status === 'loading' ? 'Sending' : 'Send message'}

                    {status === 'loading' ? null : (
                        <ArrowRight
                            aria-hidden='true'
                            className='size-4 transition-transform duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] group-hover/button:translate-x-0.5'
                        />
                    )}
                </ActionButton>

                <p
                    role='status'
                    aria-live='polite'
                    className={cn(
                        'text-[13px] tracking-[-0.01em]',
                        status === 'error' ? 'text-[#ff6a5c]' : 'text-white/40'
                    )}
                >
                    {message || 'We reply within one business day.'}
                </p>
            </div>
        </form>
    )
}
